import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { RefreshCw, Landmark } from 'lucide-react';
import { Link } from 'react-router-dom';
import './BankBalances.css';
import { money } from '../utils/money';

const API_BASE = '/api';
const rm = (n) => money(Number(n) || 0);

export default function BankBalances({ month }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const headers = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const monthQS = month ? `?month=${month}` : '';

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/bank-balances${month ? `?month=${month}` : ''}`, { headers: headers() });
      setData(res.data);
      setError(null);
    } catch (e) {
      setError('Failed to load bank balances');
    }
  }, [headers, month]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setBusy(true); setError(null);
    try {
      const res = await axios.post(`${API_BASE}/bank-balances/sync${monthQS}`, {}, { headers: headers() });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Refresh failed');
    } finally {
      setBusy(false);
    }
  };

  // Before the first successful load: surface an error if one occurred, else render nothing.
  if (!data) {
    return error
      ? <div className="bank-balances-card" role="alert"><p>{error}. Your accounts have not been replaced with zero.</p><button className="btn-sm" onClick={load}>Try again</button></div>
      : null;
  }

  const stale = data.synced_at && (Date.now() - new Date(data.synced_at).getTime()) > 26 * 3600 * 1000;
  const updated = data.synced_at
    ? new Date(data.synced_at).toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur', dateStyle: 'medium', timeStyle: 'short' })
    : '—';

  return (
    <div className="bank-balances-card">
      <div className="bank-balances-head">
        <h3><Landmark size={18} /> Bank Balances</h3>
        <button className="btn-sm" onClick={refresh} disabled={busy} title="Refresh from Bukku">
          <RefreshCw size={14} /> {busy ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && <div className="alert alert-error" role="alert">{error}. Previously loaded balances are still shown.</div>}

      <div className="bank-balances-total-row">
        <span className="bank-balances-total">{rm(data.total)}</span>
        {runwayBadge(data.runway_months)}
      </div>
      <div className="bank-balances-updated">
        Last accounting sync: {updated}{stale && <span className="bank-balances-stale"> · over a day old — refresh before making a decision</span>}
        {data.fixed_cost ? <> · Fixed cost {rm(data.fixed_cost)}/mo</> : null}
      </div>
      <p className="bank-balances-explainer">Accounting balances, not live bank availability. The sync date is separate from your selected report month.</p>
      {data.runway_months != null && data.runway_months < 3 && <p className="bank-balances-action">Balances cover less than three months of the entered fixed costs. <Link to="/forecast">Review upcoming costs and targets →</Link></p>}

      {data.accounts.length === 0 ? (
        <p className="bank-balances-empty">No accounts selected. <Link to="/settings/bank-accounts">Choose accounts to display →</Link></p>
      ) : (
        <div className="bank-balances-groups">
          {groupByCompany(data.accounts).map(group => (
            <div className="bank-balances-group" key={group.company}>
              <div className="bank-balances-group-head">
                <span className="bank-balances-company">{group.company}</span>
                <span className="bank-balances-subtotal">{rm(group.subtotal)}</span>
              </div>
              <details className="bank-account-details"><summary>View {group.accounts.length} {group.accounts.length === 1 ? 'account' : 'accounts'}</summary><ul className="bank-balances-list">
                {group.accounts.map(a => (
                  <li key={`${a.company}|${a.bukku_code}`}>
                    <span className="bank-balances-name">{a.name}</span>
                    <span className="bank-balances-amt">{rm(a.balance)}</span>
                  </li>
                ))}
              </ul></details>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Explain the existing accounting-balance / fixed-cost estimate without
// making an unsupported judgment about overall business health.
function runwayBadge(runway) {
  if (runway == null || !Number.isFinite(Number(runway))) return null;
  const tier = runway < 3 ? 'red' : runway < 6 ? 'yellow' : 'green';
  return (
    <span className={`bank-runway ${tier}`} title="Accounting balances divided by entered monthly operating costs; not a cash-flow forecast">
      <span className="bank-runway-mo">{Number(runway).toFixed(1)} months</span> of fixed costs · estimate
    </span>
  );
}

// Group displayed accounts by company (preserving the API's order), with a
// per-company subtotal.
function groupByCompany(accounts) {
  const order = [];
  const map = {};
  for (const a of accounts) {
    const c = a.company || 'RunningMen';
    if (!map[c]) { map[c] = { company: c, accounts: [], subtotal: 0 }; order.push(c); }
    map[c].accounts.push(a);
    map[c].subtotal += parseFloat(a.balance || 0);
  }
  return order.map(c => map[c]);
}
